import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiMail, FiLock, FiArrowRight, FiArrowLeft, FiShield, FiTrendingUp, FiUsers, FiEye, FiEyeOff } from 'react-icons/fi';
import styles from './Login.module.css';
import OtpVerify from '../../components/OtpVerify/OtpVerify';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { login } from '../../features/auth/authSlice';
import { useTheme } from '../../app/ThemeContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { status, error, pendingVerificationEmail } = useAppSelector((state) => state.auth);
  const { setTheme } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(login({ email, password }));
    if (login.fulfilled.match(result)) {
      setTheme('dark');
      const role = result.payload.user.role;
      navigate(role === 'admin' ? '/admin' : '/dashboard');
    }
  };

  return (
    <div className={styles.wrap}>
      <Link to="/" className={styles.backLink}>
        <FiArrowLeft /> Back to website
      </Link>

      <div className={styles.brandPanel}>
        <div className={styles.gridOverlay} />
        <div className={`${styles.blob} ${styles.blob1}`} />
        <div className={`${styles.blob} ${styles.blob2}`} />
        <div className={`${styles.blob} ${styles.blob3}`} />

        <div className={styles.brandContent}>
          <div className={styles.brandEyebrow}>Block Cost Technologies</div>
          <h2 className={styles.brandTitle}>
            Track every <span>meeting</span> &amp; <span>investment</span> request in one place.
          </h2>
          <p className={styles.brandSub}>
            Sign in to see live status on your submissions, manage your profile, and stay in sync
            with the BCT team.
          </p>

          <div className={styles.chips}>
            <div className={styles.chip} style={{ animationDelay: '.1s' }}>
              <div className={styles.chipIcon}><FiTrendingUp /></div>
              <div className={styles.chipText}>
                <b>Your capital, working</b>
                <span>Track Portal A/B/C payouts, live</span>
              </div>
            </div>
            <div className={styles.chip} style={{ animationDelay: '.25s' }}>
              <div className={styles.chipIcon}><FiUsers /></div>
              <div className={styles.chipText}>
                <b>Grow with your network</b>
                <span>Referral bonuses from your whole team</span>
              </div>
            </div>
            <div className={styles.chip} style={{ animationDelay: '.4s' }}>
              <div className={styles.chipIcon}><FiShield /></div>
              <div className={styles.chipText}>
                <b>Bank-grade protection</b>
                <span>Your funds and data, fully secured</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.formPanel}>
        {pendingVerificationEmail ? (
          <OtpVerify email={pendingVerificationEmail} />
        ) : (
        <div className={styles.card}>
          <div className={styles.eyebrow}>BCT Platform</div>
          <h1 className={styles.title}>Welcome back</h1>
          <p className={styles.subtitle}>Sign in to view your requests and status.</p>

          {error && <div className={styles.error}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className={styles.field}>
              <label className={styles.label}>Email</label>
              <div className={styles.inputWrap}>
                <FiMail className={styles.inputIcon} />
                <input
                  type="email"
                  className={styles.input}
                  placeholder="support@blockcostlabs.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Password</label>
              <div className={styles.inputWrap}>
                <FiLock className={styles.inputIcon} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={styles.input}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button type="button" className={styles.eyeToggle} onClick={() => setShowPassword((v) => !v)} tabIndex={-1}>
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              <div style={{ textAlign: 'right', marginTop: 8 }}>
                <Link to="/forgot-password" style={{ fontSize: 12.5, color: 'var(--muted)' }}>Forgot password?</Link>
              </div>
            </div>

            <button type="submit" className={styles.submitBtn} disabled={status === 'loading'}>
              {status === 'loading' ? <span className={styles.spinner} /> : <>Sign in <FiArrowRight /></>}
            </button>
          </form>

          <div className={styles.footer}>
            New here? <Link to="/register">Create an account</Link>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default Login;